import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Phone, MessageCircle } from "lucide-react";
import FAQSection from "../sections/FAQSection";
import { PHONE_TEL, WHATSAPP_URL, PHOTO_ABOUT } from "../data/site";
import SEO from "../seo/SEO";
import {
  physicianSchema,
  medicalClinicSchemas,
  faqPageSchema,
  breadcrumbSchema,
  webPageSchema,
} from "../seo/schemas";

/**
 * Reusable editorial template for Symptom / Disease / Procedure pages.
 *
 * Props:
 *  kind: "Symptom" | "Condition" | "Procedure"
 *  title (h1 — make it SEO-optimised with disease + location)
 *  subtitle (lede)
 *  breadcrumb: [{label, to}]
 *  sections: [{ id, eyebrow, heading, body, bullets?, sidenote? }]
 *    First 4 sections render as h2, the rest as h3 (SEO-clean hierarchy).
 *  faq: [{q, a}]
 *  related: [{ label, to }]
 *  seo: { title, description, canonical, extraSchemas: [] }
 */
export default function DetailTemplate({
  kind,
  title,
  subtitle,
  breadcrumb = [],
  sections = [],
  faq = [],
  related = [],
  seo = {},
}) {
  const breadcrumbItems = [
    { label: "Home", url: "/" },
    ...breadcrumb.map((b) => ({ label: b.label, url: b.to })),
  ];

  const schemas = [
    physicianSchema(),
    ...medicalClinicSchemas(),
    breadcrumbSchema(breadcrumbItems),
    webPageSchema({
      name: seo.title || title,
      description: seo.description || subtitle,
      url: seo.canonical || "/",
    }),
    ...(faq.length > 0 ? [faqPageSchema(faq)] : []),
    ...(seo.extraSchemas || []),
  ];

  return (
    <main data-testid={`${kind.toLowerCase()}-detail-page`}>
      <SEO
        title={seo.title || `${title} | Dr. Indresh Dixit, Varanasi`}
        description={seo.description || subtitle}
        canonical={seo.canonical}
        schemas={schemas}
      />

      {/* Breadcrumb */}
      <div className="container-x pt-8 md:pt-10">
        <nav
          data-testid="breadcrumb"
          className="flex items-center gap-2 text-[12px] text-black/55"
        >
          <Link to="/" className="hover:text-black">
            Home
          </Link>
          {breadcrumb.map((b, i) => (
            <span key={i} className="flex items-center gap-2">
              <ChevronRight className="h-3 w-3 text-black/40" />
              {b.to ? (
                <Link to={b.to} className="hover:text-black">
                  {b.label}
                </Link>
              ) : (
                <span className="text-black/85 font-medium">{b.label}</span>
              )}
            </span>
          ))}
        </nav>
      </div>

      {/* Header */}
      <section className="border-b border-black/10">
        <div className="container-x py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="overline">{kind} · Varanasi</div>
            <h1
              data-testid="detail-h1"
              className="font-serif-display mt-5 text-4xl sm:text-5xl lg:text-[64px] leading-[1.02] tracking-tight"
            >
              {title}
            </h1>
            <p className="mt-7 text-black/70 text-[17px] leading-relaxed max-w-2xl">
              {subtitle}
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary group"
                data-testid="detail-book-btn"
              >
                <MessageCircle className="h-4 w-4" /> Book Appointment
              </a>
              <a
                href={PHONE_TEL}
                className="btn-ghost group"
                data-testid="detail-call-btn"
              >
                <Phone className="h-4 w-4" /> Call Now
              </a>
            </div>
          </div>
          <div className="lg:col-span-4">
            <aside className="border border-black/10 rounded-[6px] p-6 bg-[#fbfaf7] sticky top-24">
              <div className="overline">Doctor Insight</div>
              <div className="mt-4 flex items-center gap-3">
                <img
                  src={PHOTO_ABOUT}
                  alt="Dr. Indresh Dixit — DM Gastroenterologist, Varanasi"
                  className="h-12 w-12 rounded-full object-cover border border-black/10"
                />
                <div>
                  <div className="font-semibold text-[14px]">
                    Dr. Indresh Dixit
                  </div>
                  <div className="text-[11px] tracking-[0.15em] uppercase text-black/55">
                    DM Gastroenterology, BHU
                  </div>
                </div>
              </div>
              <p className="mt-5 text-[14px] text-black/70 leading-relaxed">
                &ldquo;Most {kind.toLowerCase()}s feel scary because of the
                uncertainty, not the disease. The first job is a clear
                diagnosis. The plan follows naturally.&rdquo;
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold border-b border-black pb-1"
                data-testid="detail-doctor-link"
              >
                Read full profile <ArrowUpRight className="h-4 w-4 arrow" />
              </Link>
            </aside>
          </div>
        </div>
      </section>

      {/* Content sections */}
      <article className="container-x py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-3 hidden lg:block">
          <div className="sticky top-24">
            <div className="overline">On this page</div>
            <ul className="mt-5 space-y-3 text-[14px]">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-black/70 hover:text-[#0370AD] border-l-2 border-black/10 hover:border-[#0370AD] pl-3 block transition-colors"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="lg:col-span-9 space-y-14">
          {sections.map((s, i) => {
            // Limit to 3 H2 + the FAQ H2 = 4 H2 on the page. Rest as H3.
            const HTag = i < 3 ? "h2" : "h3";
            return (
              <section
                key={s.id}
                id={s.id}
                data-testid={`detail-section-${s.id}`}
                className="scroll-mt-24"
              >
                {s.eyebrow && <div className="overline">{s.eyebrow}</div>}
                <HTag className="font-serif-display mt-3 text-3xl md:text-[40px] tracking-tight leading-[1.1]">
                  {s.heading}
                </HTag>
                {s.body && (
                  <div className="mt-6 text-black/75 text-[16px] leading-relaxed space-y-4 max-w-3xl">
                    {typeof s.body === "string"
                      ? s.body
                          .split("\n\n")
                          .map((p, idx) => <p key={idx}>{p}</p>)
                      : s.body}
                  </div>
                )}
                {s.bullets && (
                  <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                    {s.bullets.map((b, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 border-b border-dashed border-black/10 pb-3 text-[15px] text-black/80"
                      >
                        <span className="text-[11px] tracking-[0.18em] uppercase text-[#0370AD] font-semibold pt-1">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.sidenote && (
                  <div className="mt-7 border-l-2 border-[#0370AD] bg-[#EAF6FD] p-5 text-[14.5px] text-black/80 leading-relaxed">
                    {s.sidenote}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </article>

      {/* CTA */}
      <section className="border-y border-black/10 bg-[#0a0a0a] text-white">
        <div className="container-x py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 items-center gap-8">
          <div className="md:col-span-8">
            <div className="overline !text-[#7ec3e6]">Next Step</div>
            <p className="font-serif-display mt-3 text-3xl md:text-5xl leading-tight">
              Get a clear, focused consult with Dr. Dixit.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-3 md:justify-end">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="cta-book-bottom"
              className="btn-primary"
            >
              Book Appointment
            </a>
            <a
              href={PHONE_TEL}
              data-testid="cta-call-bottom"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/30 text-white font-semibold text-[14px] hover:bg-white hover:text-black transition-colors"
            >
              <Phone className="h-4 w-4" /> Call
            </a>
          </div>
        </div>
      </section>

      {faq.length > 0 && (
        <FAQSection
          items={faq}
          eyebrow="On this topic"
          title="Common questions, answered."
          headingTag="h2"
        />
      )}

      {related.length > 0 && (
        <section className="section-pad">
          <div className="container-x">
            <div className="overline">Related Reading</div>
            <h3 className="font-serif-display mt-4 text-3xl md:text-5xl tracking-tight">
              Explore further.
            </h3>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((r, idx) => (
                <Link
                  key={`${r.to}-${idx}`}
                  to={r.to}
                  data-testid={`related-${idx}`}
                  className="group edit-card flex items-center justify-between"
                >
                  <span className="font-display-semibold text-xl md:text-2xl">
                    {r.label}
                  </span>
                  <ArrowUpRight className="h-4 w-4 arrow text-[#0370AD]" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
